import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiIncrementalityTestPayload,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";
import { Alert, Box, Stack } from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import Loading from "Components/Loading/Loading";
import { useSelectedProject } from "Hooks/useSelectedProject";
import { IChart, TChartData } from "Interfaces";
import { IFromToChartOutput } from "Providers/FromToProvider";
import { useChartData, StyledComposedChart } from "features/analytics";
import { useMemo } from "react";
import {
  AUTO_MATCHED_CONTROL_LABEL,
  buildTestSeriesLabel,
  PERFORMANCE_CHART_FOOTNOTE,
  PERFORMANCE_CHART_SUBTITLE,
  PERFORMANCE_OVER_TIME_TITLE,
  TEST_PERIOD_STARTS_LABEL,
} from "../helpers/copy";
import { SectionSubTitle, SectionTitle } from "./styled";
import moment from "moment";
import { useTheme } from "@mui/material/styles";

interface IIncrementalityPerformanceChartProps {
  incrementalityTest: ApiIncrementalityTestPayload;
  primaryMetricId: ApiMetric;
}

type TrendAccumulatorRow = {
  name: string;
  _controlCount: number;
} & Record<string, string | number>;

function IncrementalityPerformanceChart({
  incrementalityTest,
  primaryMetricId,
}: IIncrementalityPerformanceChartProps) {
  const theme = useTheme();
  const { selectedProject } = useSelectedProject();
  const selectedCities = useMemo(
    () =>
      [
        incrementalityTest.testRegionCity,
        ...((incrementalityTest.controlRegions || []).map((r) => r.city) as
          | string[]
          | []),
      ].filter(Boolean) as string[],
    [incrementalityTest.controlRegions, incrementalityTest.testRegionCity],
  );

  const chart = useMemo<IChart>(
    () => ({
      name: "Performance over time",
      description: "Indexed to control period mean (=100)",
      dimension: [
        ApiDimension.DIMENSION_SESSION_START_BY_DAY,
        ApiDimension.DIMENSION_CITY,
      ],
      template: ApiChartTemplate.TREND,
      type: ApiChartType.GRAPH,
      comparison: false,
      limit: 5000,
      dateDimension: ApiDimension.DIMENSION_SESSION_START_BY_DAY,
      attributes: [
        ...(incrementalityTest.testRegionCountry
          ? [
              {
                key: ApiDimension.DIMENSION_COUNTRY,
                value: incrementalityTest.testRegionCountry,
              },
            ]
          : []),
        ...selectedCities.map((city) => ({
          key: ApiDimension.DIMENSION_CITY,
          operator: ApiOperator.EQUAL,
          value: city,
        })),
      ],
      yAxisKeys: [
        {
          key: "l",
          fields: [primaryMetricId],
          chart: {
            [primaryMetricId]: ApiYAxisChartType.LINE,
          },
        },
      ],
      displayOptions: {
        noFilters: true,
        noLegend: false,
      },
    }),
    [incrementalityTest.testRegionCountry, primaryMetricId, selectedCities],
  );

  const dateOverride = useMemo<IFromToChartOutput | null>(() => {
    if (!incrementalityTest.preTestFrom || !incrementalityTest.testTo) {
      return null;
    }
    return {
      from: new Date(incrementalityTest.preTestFrom),
      to: new Date(incrementalityTest.testTo),
      lastNDays: undefined,
    };
  }, [incrementalityTest.preTestFrom, incrementalityTest.testTo]);

  const { chartData, loading } = useChartData(
    selectedProject,
    chart,
    dateOverride,
    { ignoreModel: true },
  );

  const transformedData = useMemo(() => {
    const rows = chartData?.data || [];
    const testCity = incrementalityTest.testRegionCity;
    if (!rows.length || !testCity) return [];

    const controlCitySet = new Set(
      (incrementalityTest.controlRegions || [])
        .map((region) => region.city)
        .filter(Boolean) as string[],
    );
    const testSeriesLabel = buildTestSeriesLabel(testCity);
    const controlSeriesLabel = AUTO_MATCHED_CONTROL_LABEL;
    const preFrom = incrementalityTest.preTestFrom
      ? new Date(incrementalityTest.preTestFrom)
      : null;
    const preTo = incrementalityTest.preTestTo
      ? new Date(incrementalityTest.preTestTo)
      : null;

    const metricKey = primaryMetricId;

    const seriesByDate = new Map<string, TrendAccumulatorRow>();

    rows.forEach((row) => {
      const name = String(row.name || "");
      if (!name) return;
      const city = String(row[ApiDimension.DIMENSION_CITY] || "").trim();
      const metricValue = Number(row[metricKey] || 0);
      if (!city || !Number.isFinite(metricValue)) return;

      const current: TrendAccumulatorRow = seriesByDate.get(name) || {
        name,
        [testSeriesLabel]: 0,
        [controlSeriesLabel]: 0,
        _controlCount: 0,
      };

      if (city === testCity) {
        current[testSeriesLabel] =
          Number(current[testSeriesLabel] || 0) + metricValue;
      } else if (controlCitySet.has(city)) {
        current[controlSeriesLabel] =
          Number(current[controlSeriesLabel] || 0) + metricValue;
        current._controlCount = Number(current._controlCount || 0) + 1;
      }

      seriesByDate.set(name, current);
    });

    const series: TrendAccumulatorRow[] = Array.from(seriesByDate.values()).map(
      (row) => ({
        ...row,
        [controlSeriesLabel]:
          Number(row._controlCount) > 0
            ? Number(row[controlSeriesLabel]) / Number(row._controlCount)
            : 0,
      }),
    );

    const inPreRange = (dateValue: string) => {
      if (!preFrom || !preTo) return false;
      const d = new Date(dateValue);
      if (Number.isNaN(d.getTime())) return false;
      return d >= preFrom && d <= preTo;
    };

    const preRows = series.filter((row) => inPreRange(String(row.name)));

    if (preRows.length === 0) {
      console.warn(
        "[transformedData] No pre-test rows found — check preTestFrom/preTestTo against your date format.",
      );
    }

    const testBaseline =
      preRows.reduce((acc, row) => acc + Number(row[testSeriesLabel] || 0), 0) /
      Math.max(preRows.length, 1);

    const controlBaseline =
      preRows.reduce(
        (acc, row) => acc + Number(row[controlSeriesLabel] || 0),
        0,
      ) / Math.max(preRows.length, 1);

    const minBaseline = 1e-6;

    return series.map((row) => ({
      name: row.name,
      [testSeriesLabel]:
        testBaseline > minBaseline
          ? (Number(row[testSeriesLabel] || 0) / testBaseline) * 100
          : 0,
      [controlSeriesLabel]:
        controlBaseline > minBaseline
          ? (Number(row[controlSeriesLabel] || 0) / controlBaseline) * 100
          : 0,
    }));
  }, [
    chartData?.data,
    incrementalityTest.controlRegions,
    incrementalityTest.preTestFrom,
    incrementalityTest.preTestTo,
    incrementalityTest.testRegionCity,
    primaryMetricId,
  ]);

  const testStartLine = useMemo(
    () =>
      incrementalityTest.testFrom
        ? [
            {
              x: moment(incrementalityTest.testFrom).format("DD MMM YYYY"),
              label: TEST_PERIOD_STARTS_LABEL,
              stroke: theme.palette.primary.main,
            },
          ]
        : [],
    [incrementalityTest.testFrom, theme.palette.primary.main],
  );

  const testSeriesLabel = buildTestSeriesLabel(
    incrementalityTest.testRegionCity,
  );

  return (
    <GlassCard boxProps={{ p: 2 }} noShadow>
      <Stack spacing={2}>
        <Box>
          <SectionTitle>{PERFORMANCE_OVER_TIME_TITLE}</SectionTitle>
          <SectionSubTitle>{PERFORMANCE_CHART_SUBTITLE}</SectionSubTitle>
        </Box>
        <Box sx={{ height: 320 }}>
          {loading ? (
            <Loading />
          ) : (
            <StyledComposedChart
              data={transformedData as TChartData[]}
              noReOrder
              showTooltip
              dimensions={[ApiDimension.DIMENSION_SESSION_START_BY_DAY]}
              verticalReferenceLines={testStartLine}
              yAxisKeys={[
                {
                  key: "trend",
                  fields: [testSeriesLabel, AUTO_MATCHED_CONTROL_LABEL],
                  chart: {
                    [testSeriesLabel]: ApiYAxisChartType.LINE,
                    [AUTO_MATCHED_CONTROL_LABEL]: ApiYAxisChartType.LINE,
                  },
                },
              ]}
            />
          )}
        </Box>
        <Alert severity="info">{PERFORMANCE_CHART_FOOTNOTE}</Alert>
      </Stack>
    </GlassCard>
  );
}

export default IncrementalityPerformanceChart;
